<div class="projekt-rechner">
  <div class="projekt-rechner__hinweise">
    @for (hinweis of hinweise; track hinweis) {
      <div class="projekt-rechner__hinweis" data-testid="rechner-hinweis">
        <div class="projekt-rechner__hinweis-titel">
          {{ 'projektRechner.hinweise.' + hinweis + '.titel' | translate }}
        </div>
        <div class="projekt-rechner__hinweis-text">
          {{ 'projektRechner.hinweise.' + hinweis + '.text' | translate }}
        </div>
      </div>
    }
  </div>

  <div class="projekt-rechner__karten" [class.projekt-rechner__karten--einzeln]="!canManage()">
    @if (canManage()) {
      <section class="projekt-rechner__karte projekt-rechner__karte--verbinden" data-testid="rechner-verbinden">
        <h3 class="projekt-rechner__karten-titel">{{ 'projektRechner.verbinden.titel' | translate }}</h3>
        <ol class="projekt-rechner__schritte">
          <li>{{ 'projektRechner.verbinden.schritte.herunterladen' | translate }}</li>
          <li>{{ 'projektRechner.verbinden.schritte.starten' | translate }}</li>
          <li>{{ 'projektRechner.verbinden.schritte.eingeben' | translate }}</li>
        </ol>

        @if (packagesState() === 'failed') {
          <p class="projekt-rechner__meldung" role="alert" data-testid="rechner-pakete-fehler">
            {{ 'runnerPairing.packages.loadFailed' | translate }}
          </p>
        } @else if (packagesState() === 'loaded' && packages().length === 0) {
          <p
            class="projekt-rechner__meldung projekt-rechner__meldung--info"
            role="status"
            data-testid="rechner-pakete-keine"
          >
            {{ 'runnerPairing.packages.none' | translate }}
          </p>
        } @else if (packages().length > 0) {
          <div class="projekt-rechner__pakete">
            @for (platform of packages(); track platform) {
              <button
                mat-stroked-button
                type="button"
                class="projekt-rechner__paket"
                data-testid="rechner-paket"
                [disabled]="downloading() !== null"
                (click)="download(platform)"
              >
                @if (downloading() === platform) {
                  <mat-progress-spinner mode="indeterminate" diameter="14" />
                } @else {
                  <span aria-hidden="true">↓</span>
                }
                {{ 'runnerPairing.platform.' + platform | translate }}
              </button>
            }
          </div>
        }

        <label class="projekt-rechner__code-label" for="projekt-rechner-code">
          {{ 'projektRechner.verbinden.codeLabel' | translate }}
        </label>
        <div class="projekt-rechner__code-zeile">
          <input
            #codeFeld
            id="projekt-rechner-code"
            class="projekt-rechner__code"
            data-testid="rechner-code"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            [attr.aria-label]="'projektRechner.verbinden.codeLabel' | translate"
            [placeholder]="'projektRechner.verbinden.codePlaceholder' | translate"
            [value]="code()"
            (input)="onCodeInput(codeFeld)"
            (keydown.enter)="$event.preventDefault(); submit()"
          />
          <button
            mat-flat-button
            type="button"
            class="projekt-rechner__verbinden"
            data-testid="rechner-verbinden-knopf"
            [disabled]="!canSubmit()"
            (click)="submit()"
          >
            @if (busy()) {
              <mat-progress-spinner mode="indeterminate" diameter="16" />
            }
            {{ 'projektRechner.verbinden.knopf' | translate }}
          </button>
        </div>
        <p class="projekt-rechner__code-hinweis" data-testid="rechner-code-hinweis">
          {{ 'projektRechner.verbinden.hinweis' | translate }}
        </p>

        @if (connectedRunner(); as runnerName) {
          <p
            class="projekt-rechner__meldung projekt-rechner__meldung--ok"
            role="status"
            data-testid="rechner-verbunden"
          >
            {{ 'projektRechner.verbinden.erfolg' | translate: { name: runnerName } }}
          </p>
        }
        @if (waitingForRunner()) {
          <p class="projekt-rechner__meldung projekt-rechner__meldung--info" role="status" data-testid="rechner-wartet">
            {{ 'projektRechner.verbinden.wartet' | translate }}
          </p>
        }
        @if (runnerNotReported()) {
          <p
            class="projekt-rechner__meldung projekt-rechner__meldung--info"
            role="status"
            data-testid="rechner-meldet-sich-nicht"
          >
            {{ 'projektRechner.verbinden.meldetSichNicht' | translate }}
          </p>
        }
        @if (refreshFailed()) {
          <p class="projekt-rechner__meldung" role="alert" data-testid="rechner-nachladen-fehler">
            {{ 'projektRechner.verbinden.nachladenFehler' | translate }}
          </p>
        }
        @if (connectError(); as messageKey) {
          <p class="projekt-rechner__meldung" role="alert" data-testid="rechner-verbinden-fehler">
            {{ messageKey | translate }}
          </p>
        }
      </section>
    }

    <section class="projekt-rechner__karte projekt-rechner__karte--liste" data-testid="rechner-liste">
      <h3 class="projekt-rechner__karten-titel projekt-rechner__karten-titel--liste">
        {{ 'projektRechner.liste.titel' | translate }}
      </h3>
      @switch (listState()) {
        @case ('loading') {
          <p class="projekt-rechner__zustand" role="status" data-testid="rechner-laedt">
            <mat-progress-spinner mode="indeterminate" diameter="16" />
            {{ 'projektRechner.liste.laedt' | translate }}
          </p>
        }
        @case ('failed') {
          <div class="projekt-rechner__zustand" role="alert" data-testid="rechner-ladefehler">
            <span>{{ 'projektRechner.liste.ladefehler' | translate }}</span>
            <button mat-stroked-button type="button" data-testid="rechner-erneut" (click)="reload()">
              {{ 'projektRechner.liste.erneut' | translate }}
            </button>
          </div>
        }
        @default {
          @if (detachFailed()) {
            <p
              class="projekt-rechner__meldung projekt-rechner__meldung--liste"
              role="alert"
              data-testid="rechner-trennen-fehler"
            >
              {{ 'projektRechner.trennen.fehler' | translate }}
            </p>
          }
          @for (runner of runners(); track runner.id) {
            <div class="projekt-rechner__zeile" data-testid="rechner-zeile">
              <span
                class="projekt-rechner__punkt"
                [class.projekt-rechner__punkt--online]="runner.online"
                data-testid="rechner-punkt"
                aria-hidden="true"
              ></span>
              <div class="projekt-rechner__zeile-inhalt">
                <div class="projekt-rechner__zeile-kopf">
                  <code class="projekt-rechner__name">{{ runner.name }}</code>
                  <span
                    class="projekt-rechner__art"
                    [class.projekt-rechner__art--global]="runner.global"
                    data-testid="rechner-art"
                    [title]="
                      (runner.global ? 'projektRechner.art.globalTitel' : 'projektRechner.art.projektTitel') | translate
                    "
                  >
                    {{ (runner.global ? 'projektRechner.art.global' : 'projektRechner.art.projekt') | translate }}
                  </span>
                </div>
                <div class="projekt-rechner__unterzeile" data-testid="rechner-unterzeile">
                  {{ 'projektRechner.zeile.seit' | translate: { datum: runner.assignedAt | date: 'dd.MM.yyyy' } }}
                  · {{ (runner.online ? 'projektRechner.zeile.online' : 'projektRechner.zeile.offline') | translate }}
                  @if (runner.global && runner.setUpBy) {
                    · {{ 'projektRechner.zeile.angelegtVon' | translate: { name: runner.setUpBy.displayName } }}
                  }
                </div>
              </div>
              @if (canManage()) {
                <button
                  type="button"
                  class="projekt-rechner__trennen"
                  data-testid="rechner-trennen"
                  [disabled]="detaching() !== null"
                  [title]="
                    (runner.global ? 'projektRechner.trennen.titelGlobal' : 'projektRechner.trennen.titelProjekt')
                      | translate
                  "
                  [attr.aria-label]="'projektRechner.trennen.aria' | translate: { name: runner.name }"
                  (click)="detach(runner)"
                >
                  {{ 'projektRechner.trennen.knopf' | translate }}
                </button>
              }
            </div>
          } @empty {
            <p class="projekt-rechner__zustand" data-testid="rechner-leer">
              {{ 'projektRechner.liste.leer' | translate }}
            </p>
          }
        }
      }
    </section>
  </div>
</div>
